import React, { useMemo, useState } from 'react';
import './MoodGames.css';

/**
 * OddOne — "Find the Odd One". A grid of the same symbol with a single different
 * one hiding among them; tap it to clear the round. Three quick rounds get a touch
 * harder (bigger grid), then it celebrates. A light focus warm-up, easy to leave.
 */
const PAIRS = [
  { base: '◆', odd: '◇' },
  { base: '●', odd: '◐' },
  { base: '▲', odd: '△' },
  { base: '★', odd: '☆' },
  { base: '■', odd: '□' },
];
const ROUNDS = [{ cols: 3, n: 9 }, { cols: 4, n: 16 }, { cols: 5, n: 25 }];

const buildRound = (round: number) => {
  const { n } = ROUNDS[round];
  const pair = PAIRS[Math.floor(Math.random() * PAIRS.length)];
  const oddAt = Math.floor(Math.random() * n);
  return { pair, oddAt, n, cols: ROUNDS[round].cols };
};

export const OddOne: React.FC = () => {
  const [round, setRound] = useState(0);
  const [wrong, setWrong] = useState<number | null>(null);
  const [right, setRight] = useState<number | null>(null);
  const done = round >= ROUNDS.length;

  const board = useMemo(() => (done ? null : buildRound(round)), [round, done]);

  const pick = (i: number) => {
    if (!board || right !== null) return;
    if (i === board.oddAt) {
      setRight(i);
      window.setTimeout(() => { setRight(null); setRound((r) => r + 1); }, 450);
    } else {
      setWrong(i);
      window.setTimeout(() => setWrong(null), 320);
    }
  };

  const replay = () => { setRound(0); setWrong(null); setRight(null); };

  return (
    <div className="mm-game mm-game--odd">
      <div className="mm-game__head">
        <h3 className="mm-game__title">Find the Odd One</h3>
        <p className="mm-game__sub">Tap the symbol that’s a little different.</p>
      </div>

      {done || !board ? (
        <div className="mm-game__done">
          <span className="mm-game__done-emoji">🎯</span>
          <span className="mm-game__done-title">Sharp eye!</span>
          <span className="mm-game__done-sub">That’s your focus warmed up. Nicely spotted.</span>
          <button type="button" className="mm-game__btn mm-game__btn--ghost" onClick={replay}>Play again</button>
        </div>
      ) : (
        <>
          <div className="mm-game__meta">Round {round + 1} of {ROUNDS.length}</div>
          <div
            className="mm-odd__grid"
            style={{ gridTemplateColumns: `repeat(${board.cols}, 1fr)` }}
            role="group"
            aria-label="Symbol grid"
          >
            {Array.from({ length: board.n }, (_, i) => (
              <button
                key={i}
                type="button"
                className={`mm-odd__cell ${wrong === i ? 'is-wrong' : ''} ${right === i ? 'is-right' : ''}`}
                aria-label={i === board.oddAt ? 'the odd symbol' : 'symbol'}
                onClick={() => pick(i)}
              >
                {i === board.oddAt ? board.pair.odd : board.pair.base}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default OddOne;
